import type { ProductDetailDto, ProductImageDto, ProductVariantDto } from '@ecom/contracts';

/**
 * A product's fabrics: its colours, each with the photographs and variants
 * that belong to it.
 *
 * Variants carry codes (`{ color: "brass", unit: "roll-10" }`), and a fabric
 * product has one per colour and unit — seven colours on a roll of each length
 * is thirty-five rows nobody can read as a list. Grouped by colour they are
 * seven fabrics, each sold a few ways, which is how the store thinks of them
 * and how the storefront shows them (a colour's photographs are the images
 * linked to any of its variants).
 *
 * A product without a colour option has one "fabric" per variant.
 */

export const COLOR_OPTION = 'color';

export interface Fabric {
  /** The colour code; the variant id when the product has no colour option. */
  key: string;
  /** The colour code, or null when the product has none. */
  color: string | null;
  label: string;
  swatchHex: string | null;
  /** This colour's own photographs, in the product's order. */
  images: ProductImageDto[];
  /** Every variant of this colour, samples included, in the product's order. */
  variants: ProductVariantDto[];
}

/** How an option value reads: its label when someone wrote one, its code otherwise. */
export function optionLabel(product: ProductDetailDto, option: string, value: string): string {
  return product.optionValues.find((entry) => entry.option === option && entry.value === value)?.label || value;
}

/** A variant's options other than colour, as a shopper reads them — "10 m roll". */
export function detailLabel(product: ProductDetailDto, variant: ProductVariantDto): string {
  return Object.entries(variant.options)
    .filter(([option]) => option !== COLOR_OPTION)
    .map(([option, value]) => optionLabel(product, option, value))
    .join(' · ');
}

export function fabricsOf(product: ProductDetailDto): Fabric[] {
  const images = [...product.images].sort((a, b) => a.sortOrder - b.sortOrder);
  const hasColour = product.variants.some((variant) => variant.options[COLOR_OPTION]);

  if (!hasColour) {
    return product.variants.map((variant) => ({
      key: variant.id,
      color: null,
      label: detailLabel(product, variant) || variant.sku,
      swatchHex: null,
      images: images.filter((image) => image.variantId === variant.id),
      variants: [variant],
    }));
  }

  // The store's order for colours first, then any colour nobody has ordered, as first seen.
  const ranked = product.optionValues
    .filter((entry) => entry.option === COLOR_OPTION)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((entry) => entry.value);
  const colours: string[] = [];
  for (const value of [...ranked, ...product.variants.map((variant) => variant.options[COLOR_OPTION])]) {
    if (value && !colours.includes(value) && product.variants.some((variant) => variant.options[COLOR_OPTION] === value)) {
      colours.push(value);
    }
  }

  const colourOf = new Map(product.variants.map((variant) => [variant.id, variant.options[COLOR_OPTION]]));

  return colours.map((color) => ({
    key: color,
    color,
    label: optionLabel(product, COLOR_OPTION, color),
    swatchHex: product.optionValues.find((entry) => entry.option === COLOR_OPTION && entry.value === color)?.swatchHex ?? null,
    images: images.filter((image) => image.variantId !== null && colourOf.get(image.variantId) === color),
    variants: product.variants.filter((variant) => variant.options[COLOR_OPTION] === color),
  }));
}

/**
 * The variant a look should use when a fabric is picked: what is sold, never a
 * sample (R16) — the one sold by the single unit when there is one ("by the
 * metre"), else the product's default, else the first.
 */
export function defaultLookVariant(fabric: Fabric): ProductVariantDto | null {
  const sellable = fabric.variants.filter((variant) => !variant.isSample);
  return (
    sellable.find((variant) => variant.measureQuantity === 1000) ??
    sellable.find((variant) => variant.isDefault) ??
    sellable[0] ??
    null
  );
}

/** The photograph a fabric is recognised by: its own, else the product's first. */
export function fabricImage(product: ProductDetailDto, fabric: Fabric): string | null {
  return fabric.images[0]?.url ?? [...product.images].sort((a, b) => a.sortOrder - b.sortOrder)[0]?.url ?? null;
}

/**
 * What a small picture of a fabric should show: its own photograph, else
 * nothing (so its swatch shows), else — for a fabric with no colour — the
 * product's first photograph. The bolt's general photo would show an emerald
 * line in brass.
 */
export function fabricThumb(product: ProductDetailDto, fabric: Fabric): string | null {
  return fabric.images[0]?.url ?? (fabric.swatchHex ? null : fabricImage(product, fabric));
}

/** The fabric a photograph belongs to, or null when it is one of the product's general photographs. */
export function fabricOfImage(fabrics: Fabric[], image: ProductImageDto): Fabric | null {
  if (!image.variantId) return null;
  return fabrics.find((fabric) => fabric.variants.some((variant) => variant.id === image.variantId)) ?? null;
}

/**
 * A colour's code from its English name — "Emerald green" → `emerald-green`.
 * Codes are what the cart and the filters key on, so they stay Latin,
 * lower-case and short; the name a shopper reads is the label.
 */
export function colourCode(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

/**
 * A new colour's SKU from one of an existing colour's: the colour's code
 * swapped where the SKU spells it (`RTX-05-BRASS-M` → `RTX-05-EMERALD-M`),
 * appended where it does not. The operator can still change it.
 */
export function deriveSku(sku: string, fromColour: string, toColour: string): string {
  const to = toColour.toUpperCase();
  if (!fromColour) return `${sku}-${to}`;
  const pattern = new RegExp(fromColour.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
  return pattern.test(sku)
    ? sku.replace(pattern, (found) => (found === found.toUpperCase() ? to : toColour))
    : `${sku}-${to}`;
}
